import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Calendar as CalendarIcon, Clock, Users, Copy, Mic, Trash2, ExternalLink, Crown,
} from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  planned: { label: "Planifiée", className: "bg-[#FDF1E4] text-[#9A5B13] border-[#FDE2C0]" },
  in_progress: { label: "En cours", className: "bg-[#FFE4E6] text-[#9F1239] border-[#FECDD3]" },
  completed: { label: "Terminée", className: "bg-[#F5EFD8] text-[#3A2E1F] border-[#E5D9B8]" },
};

export default function MeetingsPage() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", scheduled_at: "", participants: "", language: "fr",
  });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/meetings").then(({ data }) => {
      setMeetings(data || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        scheduled_at: form.scheduled_at || null,
        participants: form.participants
          .split(",").map((s) => s.trim()).filter(Boolean),
        language: form.language,
      };
      const { data } = await api.post("/meetings", payload);
      toast.success("Réunion créée !");
      setOpen(false);
      setForm({ title: "", description: "", scheduled_at: "", participants: "", language: "fr" });
      load();
      navigate(`/app/meetings/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.code === "quota_exceeded") {
        setOpen(false);
        setQuotaOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur");
      }
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer la réunion "${title}" ?`)) return;
    await api.delete(`/meetings/${id}`);
    toast.success("Réunion supprimée");
    load();
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/app/meetings/join/${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien copié");
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Réunions</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">Vos réunions</h1>
          <p className="text-af-secondary mt-2">Créez, rejoignez et gérez vos réunions.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 px-5" data-testid="open-create-meeting">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle réunion
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-af-card border-af max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Créer une réunion</DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Label htmlFor="title">Titre *</Label>
                <Input id="title" required data-testid="meeting-title-input"
                  value={form.title} onChange={set("title")}
                  className="mt-1.5 border-af focus-visible:ring-terracotta"
                  placeholder="Réunion stratégique Q1" />
              </div>
              <div>
                <Label htmlFor="desc">Description</Label>
                <Textarea id="desc" rows={2} data-testid="meeting-desc-input"
                  value={form.description} onChange={set("description")}
                  className="mt-1.5 border-af focus-visible:ring-terracotta"
                  placeholder="Objectifs, contexte, agenda…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dt">Date & heure</Label>
                  <Input id="dt" type="datetime-local" data-testid="meeting-date-input"
                    value={form.scheduled_at} onChange={set("scheduled_at")}
                    className="mt-1.5 border-af focus-visible:ring-terracotta" />
                </div>
                <div>
                  <Label>Langue</Label>
                  <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
                    <SelectTrigger className="mt-1.5 border-af focus:ring-terracotta" data-testid="meeting-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="auto">Auto (FR/EN)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="parts">Participants (emails séparés par virgule)</Label>
                <Input id="parts" data-testid="meeting-participants-input"
                  value={form.participants} onChange={set("participants")}
                  className="mt-1.5 border-af focus-visible:ring-terracotta"
                  placeholder="a@x.com, b@y.com" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={creating} data-testid="meeting-submit-btn"
                  className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
                  {creating ? "Création…" : "Créer la réunion"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quota exceeded dialog */}
      <Dialog open={quotaOpen} onOpenChange={setQuotaOpen}>
        <DialogContent className="bg-af-card border-af max-w-md" data-testid="quota-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Crown className="h-5 w-5 text-terracotta" /> Limite d'essai atteinte
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-af-secondary leading-relaxed">
            Votre essai gratuit est limité à <strong>2 réunions</strong>. Passez au plan{" "}
            <strong>Business à 35 000 FCFA/mois</strong> pour créer des réunions illimitées et
            profiter de toutes les fonctionnalités IA.
          </p>
          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={() => setQuotaOpen(false)} data-testid="quota-dialog-cancel">
              Plus tard
            </Button>
            <Button
              type="button"
              data-testid="quota-upgrade-btn"
              onClick={() => navigate("/app/billing")}
              className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md"
            >
              <Crown className="h-4 w-4 mr-2" /> Passer au plan Business
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-af-muted">Chargement…</div>
      ) : meetings.length === 0 ? (
        <Card className="p-16 text-center bg-af-card border-af rounded-md" data-testid="meetings-empty">
          <div className="h-14 w-14 mx-auto rounded-md bg-af-subtle border border-af flex items-center justify-center mb-4">
            <Mic className="h-6 w-6 text-af-muted" />
          </div>
          <h3 className="font-display font-bold text-xl mb-2">Aucune réunion encore</h3>
          <p className="text-af-secondary mb-6">Créez votre première réunion pour démarrer.</p>
          <Button onClick={() => setOpen(true)} className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
            <Plus className="h-4 w-4 mr-2" /> Créer une réunion
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meetings.map((m) => {
            const sc = statusConfig[m.status] || statusConfig.planned;
            return (
              <Card
                key={m.id}
                data-testid={`meeting-card-${m.id}`}
                className="p-6 bg-af-card border-af rounded-md shadow-none hover:shadow-sm hover:-translate-y-0.5 transition-all flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`${sc.className} border rounded-sm font-medium`}>{sc.label}</Badge>
                  <button onClick={() => onDelete(m.id, m.title)} data-testid={`delete-${m.id}`}
                    className="text-af-muted hover:text-[#9F1239] transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Link to={`/app/meetings/${m.id}`} className="block flex-1">
                  <h3 className="font-display font-bold text-lg mb-2 line-clamp-2 hover:text-terracotta transition-colors">
                    {m.title}
                  </h3>
                  {m.description && (
                    <p className="text-sm text-af-secondary line-clamp-2 mb-4 leading-relaxed">{m.description}</p>
                  )}
                  <div className="space-y-1.5 text-xs text-af-muted">
                    {m.scheduled_at && (
                      <div className="flex items-center gap-1.5">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {new Date(m.scheduled_at).toLocaleString("fr-FR", {
                          dateStyle: "medium", timeStyle: "short",
                        })}
                      </div>
                    )}
                    {m.duration_seconds > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {Math.round(m.duration_seconds / 60)} min
                      </div>
                    )}
                    {(m.participants || []).length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        {m.participants.length} participant{m.participants.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </Link>
                <div className="mt-5 pt-4 border-t border-af flex items-center gap-2">
                  {m.status !== "completed" && (
                    <Link to={`/app/meetings/${m.id}/room`} className="flex-1" data-testid={`enter-room-${m.id}`}>
                      <Button className="w-full bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-9">
                        <Mic className="h-3.5 w-3.5 mr-1.5" /> Entrer
                      </Button>
                    </Link>
                  )}
                  {m.status === "completed" && (
                    <Link to={`/app/meetings/${m.id}`} className="flex-1" data-testid={`open-meeting-${m.id}`}>
                      <Button variant="outline" className="w-full border-af rounded-md h-9">
                        Ouvrir <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                      </Button>
                    </Link>
                  )}
                  <Button variant="outline" size="icon" onClick={() => copyLink(m.join_code)}
                    data-testid={`copy-link-${m.id}`}
                    className="border-af rounded-md h-9 w-9">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
